import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../../utils/api'

interface User {
  id: number
  username: string
  full_name: string
  email: string
  is_admin: boolean
  is_disabled: boolean
  comment: string | null
  language: string
  created_at: string
  last_login: string | null
}

interface PendingUser {
  id: number
  full_name: string
  email: string
  company: string | null
  message: string | null
  created_at: string
  status: 'pending' | 'approved' | 'rejected'
}

function UsersManager() {
  const [users, setUsers] = useState<User[]>([])
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
  const [activeTab, setActiveTab] = useState<'users' | 'pending'>('users')
  const [loading, setLoading] = useState(true)
  const [pendingLoading, setPendingLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [formData, setFormData] = useState({
    username: '',
    full_name: '',
    email: '',
    password: '',
    is_admin: false,
    is_disabled: false,
    comment: '',
    language: 'en'
  })
  const [generatedPassword, setGeneratedPassword] = useState('')

  useEffect(() => {
    fetchUsers()
    if (activeTab === 'pending') {
      fetchPendingUsers()
    }
  }, [activeTab])

  const fetchUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/admin/users')
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      const data = await response.json()
      setUsers(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const fetchPendingUsers = async () => {
    try {
      setPendingLoading(true)
      const response = await fetchWithAuth('/api/admin/pending-users')
      if (!response.ok) {
        throw new Error('Failed to fetch pending users')
      }
      const data = await response.json()
      setPendingUsers(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pending users')
    } finally {
      setPendingLoading(false)
    }
  }

  const handlePendingUserAction = async (id: number, action: 'approve' | 'reject') => {
    try {
      const response = await fetchWithAuth(`/api/admin/pending-users/${id}/${action}`, {
        method: 'POST'
      })

      if (!response.ok) {
        throw new Error(`Failed to ${action} user request`)
      }

      setSuccess(`User request ${action}d successfully`)
      fetchPendingUsers()
      
      if (action === 'approve') {
        fetchUsers() // Refresh users list if approved
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} user request`)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!formData.username || !formData.full_name || !formData.email) {
      setError('Username, full name, and email are required')
      return
    }

    if (!editingUser && !formData.password) {
      setError('Password is required for new users')
      return
    }

    try {
      const url = editingUser ? `/api/admin/users/${editingUser.id}` : '/api/admin/users'
      const method = editingUser ? 'PUT' : 'POST'
      
      const payload = {
        username: formData.username,
        full_name: formData.full_name,
        email: formData.email,
        is_admin: formData.is_admin,
        is_disabled: formData.is_disabled,
        comment: formData.comment || null,
        language: formData.language,
        ...(formData.password && { password: formData.password })
      }

      const response = await fetchWithAuth(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to save user: ${response.status} ${errorText}`)
      }

      setSuccess(editingUser ? 'User updated successfully' : 'User created successfully')
      setShowForm(false)
      setEditingUser(null)
      setFormData({
        username: '',
        full_name: '',
        email: '',
        password: '',
        is_admin: false,
        is_disabled: false,
        comment: '',
        language: 'en'
      })
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user')
    }
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setGeneratedPassword('') // Clear any generated password
    setFormData({
      username: user.username,
      full_name: user.full_name,
      email: user.email,
      password: '',
      is_admin: user.is_admin,
      is_disabled: user.is_disabled,
      comment: user.comment || '',
      language: user.language
    })
    setShowForm(true)
  }

  const handleToggleDisable = async (user: User) => {
    const action = user.is_disabled ? 'enable' : 'disable'
    if (!confirm(`Are you sure you want to ${action} this user account?`)) {
      return
    }

    try {
      const response = await fetchWithAuth(`/api/admin/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...user,
          is_disabled: !user.is_disabled
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to ${action} user`)
      }

      setSuccess(`User ${action}d successfully`)
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} user`)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to permanently delete this user? This action cannot be undone and may cause security issues if the username is reused.')) {
      return
    }

    try {
      const response = await fetchWithAuth(`/api/admin/users/${id}`, {
        method: 'DELETE'
      })

      if (!response.ok) {
        throw new Error('Failed to delete user')
      }

      setSuccess('User deleted successfully')
      setShowForm(false)
      setEditingUser(null)
      resetForm()
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    }
  }

  const generateStrongPassword = () => {
    console.log('generateStrongPassword called!') // Debug log
    const length = 16
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
    let password = ""
    
    // Ensure at least one character from each category
    const lowercase = "abcdefghijklmnopqrstuvwxyz"
    const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ"
    const numbers = "0123456789"
    const symbols = "!@#$%^&*"
    
    password += lowercase[Math.floor(Math.random() * lowercase.length)]
    password += uppercase[Math.floor(Math.random() * uppercase.length)]
    password += numbers[Math.floor(Math.random() * numbers.length)]
    password += symbols[Math.floor(Math.random() * symbols.length)]
    
    // Fill remaining length with random characters
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)]
    }
    
    // Shuffle the password to randomize positions
    const shuffled = password.split('').sort(() => Math.random() - 0.5).join('')
    
    console.log('Generated password:', shuffled) // Debug log
    setFormData({ ...formData, password: shuffled })
    setGeneratedPassword(shuffled)
    console.log('Set generatedPassword state to:', shuffled) // Debug log
  }

  const copyToClipboard = async (text: string) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        setSuccess('Password copied to clipboard!')
        setTimeout(() => setSuccess(null), 2000)
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        
        try {
          const successful = document.execCommand('copy')
          document.body.removeChild(textArea)
          
          if (successful) {
            setSuccess('Password copied to clipboard!')
            setTimeout(() => setSuccess(null), 2000)
          } else {
            throw new Error('Copy command failed')
          }
        } catch (fallbackErr) {
          document.body.removeChild(textArea)
          throw fallbackErr
        }
      }
    } catch (err) {
      setError('Failed to copy password to clipboard. Try selecting the text manually and using Ctrl+C')
      setTimeout(() => setError(null), 4000)
    }
  }

  const resetForm = () => {
    setShowForm(false)
    setEditingUser(null)
    setGeneratedPassword('')
    setFormData({
      username: '',
      full_name: '',
      email: '',
      password: '',
      is_admin: false,
      is_disabled: false,
      comment: '',
      language: 'en'
    })
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const dateOnly = date.toISOString().slice(0, 10)
    const timeOnly = date.toISOString().slice(11, 16)
    return { date: dateOnly, time: timeOnly }
  }

  if (loading) {
    return <div className="loading">Loading users...</div>
  }

  return (
    <div className="users-manager">
      <div className="page-header">
        <div className="page-title-section">
          <h2 style={{ margin: 0, color: '#2c3e50', fontSize: '1.8rem', fontWeight: '600' }}>
            👥 User Management
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.95rem' }}>
            Manage system users, permissions, and access requests
          </p>
        </div>
        
        <div className="page-actions">
          <div className="tab-navigation">
            <button 
              className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('users')}
            >
              Users ({users.length})
            </button>
            <button 
              className={`btn ${activeTab === 'pending' ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setActiveTab('pending')}
            >
              Pending Requests ({pendingUsers.length})
            </button>
          </div>
          
          {!showForm && activeTab === 'users' && (
            <button 
              className="btn btn-success" 
              onClick={() => setShowForm(true)}
              style={{ fontSize: '0.9rem', padding: '0.6rem 1.2rem' }}
            >
              ➕ Add New User
            </button>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {activeTab === 'users' && showForm && (
        <div className="form-container">
          <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Username:</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                required
                disabled={!!editingUser} // Don't allow username changes
              />
            </div>
            <div className="form-group">
              <label>Full Name:</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Password {editingUser && '(leave blank to keep current)'}:</label>
              <div className="password-input-group">
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Leave blank to keep current password' : ''}
                />
                <button
                  type="button"
                  className="btn btn-generate-password"
                  onClick={() => {
                    console.log('Button clicked!')
                    generateStrongPassword()
                  }}
                  title="Generate a secure 16-character password"
                >
                  🔐 Generate
                </button>
              </div>
              {generatedPassword && (
                <div className="generated-password-display">
                  <label style={{ fontSize: '0.9rem', marginBottom: '0.25rem', color: '#2c3e50' }}>Generated Password:</label>
                  <div className="generated-password-group">
                    <input
                      type="text"
                      value={generatedPassword}
                      readOnly
                      className="generated-password-field"
                      style={{ 
                        fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace',
                        fontSize: '0.9rem',
                        backgroundColor: '#f8f9fa',
                        border: '2px solid #27ae60',
                        color: '#2c3e50'
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-copy-password"
                      onClick={() => copyToClipboard(generatedPassword)}
                      title="Copy password to clipboard"
                    >
                      📋 Copy
                    </button>
                  </div>
                </div>
              )}
              {formData.password && (
                <div className="password-strength">
                  <small style={{ color: '#27ae60', fontWeight: '500' }}>
                    ✓ Password length: {formData.password.length} characters
                  </small>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Language:</label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
              >
                <option value="en">English</option>
                <option value="sv">Svenska (Swedish)</option>
              </select>
            </div>
            <div className="form-group">
              <label>Comment:</label>
              <textarea
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Optional notes about this user"
              />
            </div>
            <div className="form-group">
              <label>Permissions:</label>
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_admin}
                    onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  />
                  <span className="checkbox-text">Administrator privileges</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_disabled}
                    onChange={(e) => setFormData({ ...formData, is_disabled: e.target.checked })}
                  />
                  <span className="checkbox-text">Account disabled</span>
                </label>
              </div>
            </div>
            <div className="form-actions">
              <div className="primary-actions">
                <button type="submit" className="btn btn-success">
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button type="button" className="btn" onClick={resetForm}>
                  Cancel
                </button>
              </div>
              
              {editingUser && (
                <div className="danger-zone">
                  <div className="danger-zone-header">
                    <h4>⚠️ Danger Zone</h4>
                    <p>Consider using the "Account disabled" option instead to prevent security issues from username reuse.</p>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-danger"
                    onClick={() => handleDelete(editingUser.id)}
                    disabled={editingUser.username === 'admin'}
                  >
                    🗑️ Permanently Delete User
                  </button>
                </div>
              )}
            </div>
          </form>
        </div>
      )}

      {activeTab === 'pending' && (
        <div className="table-container">
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
            🔄 Pending Access Requests
          </h3>
          {pendingLoading ? (
            <div className="loading">Loading pending requests...</div>
          ) : pendingUsers.length === 0 ? (
            <div className="info-message">
              <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📝</div>
                <h4>No pending access requests</h4>
                <p>When users request access through the landing page, they will appear here for approval.</p>
              </div>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Message</th>
                  <th>Requested</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((request) => (
                  <tr key={request.id}>
                    <td><strong>{request.full_name}</strong></td>
                    <td>{request.email}</td>
                    <td>{request.company || '-'}</td>
                    <td style={{ maxWidth: '200px' }}>
                      {request.message ? (
                        <div style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>
                          {request.message.length > 100 
                            ? `${request.message.substring(0, 100)}...` 
                            : request.message}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="date-cell">
                      {(() => {
                        const dateTime = formatDate(request.created_at)
                        return typeof dateTime === 'object' ? (
                          <div>
                            <div className="date-line">{dateTime.date}</div>
                            <div className="time-line">{dateTime.time}</div>
                          </div>
                        ) : <span>{dateTime}</span>
                      })()}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn btn-sm btn-success" 
                          onClick={() => handlePendingUserAction(request.id, 'approve')}
                          title="Approve and create user account"
                        >
                          ✅ Approve
                        </button>
                        <button 
                          className="btn btn-sm btn-danger" 
                          onClick={() => handlePendingUserAction(request.id, 'reject')}
                          title="Reject this request"
                        >
                          ❌ Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'users' && (
        <div className="table-container">
          {users.length === 0 ? (
            <div className="loading">No users found</div>
          ) : (
            <table className="table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Language</th>
                <th>Last Login</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.username}</strong>
                    {user.comment && (
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.2rem' }}>
                        {user.comment}
                      </div>
                    )}
                  </td>
                  <td>{user.full_name}</td>
                  <td>{user.email}</td>
                  <td>
                    {user.is_admin ? (
                      <span className="user-badge" style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}>
                        Admin
                      </span>
                    ) : (
                      <span className="user-badge" style={{ background: 'linear-gradient(135deg, #a8e6cf, #7fcdcd)' }}>
                        User
                      </span>
                    )}
                  </td>
                  <td>
                    {user.is_disabled ? (
                      <span className="user-badge" style={{ background: 'linear-gradient(135deg, #ffb3b3, #ff6b6b)' }}>
                        Disabled
                      </span>
                    ) : (
                      <span className="user-badge" style={{ background: 'linear-gradient(135deg, #a8e6cf, #7fcdcd)' }}>
                        Active
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.8rem', fontWeight: '500' }}>
                    {user.language === 'en' ? 'EN' : user.language === 'sv' ? 'SV' : user.language.toUpperCase()}
                  </td>
                  <td className="date-cell-compact">
                    {user.last_login ? (() => {
                      const dateTime = formatDate(user.last_login)
                      return typeof dateTime === 'object' ? (
                        <div>
                          <div className="date-line">{dateTime.date}</div>
                          <div className="time-line">{dateTime.time}</div>
                        </div>
                      ) : <span>{dateTime}</span>
                    })() : (
                      <span>-</span>
                    )}
                  </td>
                  <td className="date-cell-compact">
                    {(() => {
                      const dateTime = formatDate(user.created_at)
                      return typeof dateTime === 'object' ? (
                        <div>
                          <div className="date-line">{dateTime.date}</div>
                          <div className="time-line">{dateTime.time}</div>
                        </div>
                      ) : <span>{dateTime}</span>
                    })()}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn btn-sm" 
                        onClick={() => handleEdit(user)}
                      >
                        Edit
                      </button>
                      <button 
                        className={`btn btn-sm ${user.is_disabled ? 'btn-success' : 'btn-warning'}`}
                        onClick={() => handleToggleDisable(user)}
                        disabled={user.username === 'admin'} // Protect default admin
                      >
                        {user.is_disabled ? 'Enable' : 'Disable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="info-section">
        <div className="info-card">
          <h4>🔐 Security Information</h4>
          <ul>
            <li>All passwords are securely hashed and salted using PBKDF2</li>
            <li>The default admin account cannot be deleted for system security</li>
            <li>Generated passwords contain lowercase, uppercase, numbers, and symbols</li>
          </ul>
        </div>
        
        <div className="info-card">
          <h4>👤 Permission Levels</h4>
          <ul>
            <li><strong>Admin users:</strong> Full access to admin dashboard and system management</li>
            <li><strong>Regular users:</strong> Can only view map links from their assigned sites</li>
            <li><strong>Disabled accounts:</strong> Cannot log in until re-enabled</li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default UsersManager